import { roomArea } from './customer-proposal.js';
const round2 = (n) => Math.round(n * 100) / 100;
const same = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();
export const isNoRemoval = (floor) => /^\s*none\b/i.test(floor);
/** The floor being lifted in one room: its own, else its area's, else the site's. */
export function effectiveExistingFloor(doc, room) {
    return (room.existing_floor || doc.assessment.areas.find(a => a.id === room.area_id)?.existing_floor || doc.assessment.existing_floor || '').trim();
}
/** The rooms being laid — the same selection as scopedFloorArea. */
const inScope = (doc) => doc.assessment.rooms.filter(r => !r.out_of_scope && !r.is_void && !!(r.quoted_finish || r.flooring));
/** One group per existing floor across the in-scope rooms; the site's floor first. Empty when the site floor is not recorded. */
export function removalGroups(doc) {
    const site = doc.assessment.existing_floor.trim();
    if (!site)
        return [];
    const groups = new Map();
    for (const room of inScope(doc)) {
        const floor = effectiveExistingFloor(doc, room);
        if (!floor || isNoRemoval(floor))
            continue;
        const key = floor.toLowerCase();
        const held = groups.get(key) ?? { floor, area_m2: 0, rooms: [], exception: !same(floor, site) };
        held.area_m2 += roomArea(room) ?? 0;
        held.rooms.push(room.name);
        groups.set(key, held);
    }
    return [...groups.values()].map(g => ({ ...g, area_m2: round2(g.area_m2) })).sort((a, b) => Number(a.exception) - Number(b.exception) || a.floor.localeCompare(b.floor));
}
export const removalLineId = (floor) => `remove_dispose:${floor.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)}`;
const lineName = (floor) => `Remove & dispose (${floor})`;
/**
 * Keep the removal lines in step with the existing floors. The first removal line the work panel added carries the site's
 * floor; one more line per exception floor is added, kept or dropped. Only lines this function owns (`remove_dispose:…`)
 * are ever removed; a custom line someone typed is never touched, and a rate someone set is never replaced. A quantity
 * typed by hand (`quantity_source: manual`) stays; the measured quantity is refreshed beside it.
 */
export function syncRemovalLines(doc, rates) {
    const groups = removalGroups(doc);
    const primary = doc.services.find(l => l.service_id === 'remove_dispose' && !l.id.startsWith('remove_dispose:') && l.source !== 'custom');
    if (!primary)
        return doc;
    const rateOf = (floor) => rates.find(r => same(r.floor, floor))?.sell ?? null;
    const fit = (line, g) => ({ ...line, removal_floor: g.floor, name: lineName(g.floor), measured_quantity: g.area_m2,
        quantity: line.quantity_source === 'manual' ? line.quantity : g.area_m2, rate: line.rate ?? rateOf(g.floor) });
    if (!groups.length) {
        // Nothing recorded (or everything is None): the primary returns to the whole-floor line; owned lines go.
        const { removal_floor: _drop, ...plain } = primary;
        return { ...doc, services: doc.services.filter(l => !l.id.startsWith('remove_dispose:')).map(l => l === primary ? plain : l) };
    }
    const [head, ...rest] = groups;
    const wanted = new Map(rest.map(g => [removalLineId(g.floor), g]));
    let services = doc.services.filter(l => !l.id.startsWith('remove_dispose:') || wanted.has(l.id)).map(l => {
        if (l === primary)
            return fit(l, head);
        const g = wanted.get(l.id);
        return g ? fit(l, g) : l;
    });
    for (const [id, g] of wanted) {
        if (services.some(l => l.id === id))
            continue;
        services = [...services, fit({ ...primary, id, quantity_source: 'assessment', rate: null, variation: false, offered: true, source: 'canvas' }, g)];
    }
    return { ...doc, services };
}
//# sourceMappingURL=proposal-removal.js.map